import { localStorageAvailable } from '@utils/localStorage';

export const CMS_REFER_MODAL_STORAGE_KEY = 'cms-refer-modal';

type StoredEntry = number | string | { date?: string; count: number };

const canUseStorage = () =>
  typeof window !== 'undefined' && localStorageAvailable();

const persistMap = (map: Record<string, StoredEntry>) => {
  if (!canUseStorage()) return;
  localStorage.setItem(CMS_REFER_MODAL_STORAGE_KEY, JSON.stringify(map));
};

const readMap = (): Record<string, StoredEntry> => {
  if (!canUseStorage()) return {};
  try {
    const raw = localStorage.getItem(CMS_REFER_MODAL_STORAGE_KEY);
    const parsed = raw ? JSON.parse(raw) : {};
    if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) {
      return {};
    }
    return parsed as Record<string, StoredEntry>;
  } catch {
    return {};
  }
};

const normalizeShowCount = (entry?: StoredEntry) => {
  if (entry == null) return 0;
  if (typeof entry === 'number') return entry;
  if (typeof entry === 'string') return 1;
  if (typeof entry.count === 'number') return entry.count;
  return 0;
};

const getShowCount = (userUid?: string) => {
  if (!userUid) return Number.POSITIVE_INFINITY;
  return normalizeShowCount(readMap()[userUid]);
};

/** True while the user has remaining CMS displayFrequency impressions (per device, until sign-out). */
export const canShowCmsReferModal = (
  userUid?: string,
  displayFrequency = 1
) => {
  if (!userUid || displayFrequency <= 0) return false;
  return getShowCount(userUid) < displayFrequency;
};

export const recordCmsReferModalShown = (userUid?: string) => {
  if (!userUid || !canUseStorage()) return;
  const nextCount = getShowCount(userUid) + 1;
  persistMap({ ...readMap(), [userUid]: nextCount });
};

/** Cleared on sign-out so the next session can show the modal per BE rules. */
export const clearCmsReferModalStorage = () => {
  if (!canUseStorage()) return;
  localStorage.removeItem(CMS_REFER_MODAL_STORAGE_KEY);
};
